import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { storesApi, type StoreData } from '../../lib/api';

export default function StoresTab() {
  const { t, i18n } = useTranslation();
  const [stores, setStores] = useState<StoreData[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const data = await storesApi.list();
      setStores(data);
    } catch {
      // handled by global toast
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveEdit(id: number) {
    if (!editName.trim()) return;
    try {
      await storesApi.update(id, editName.trim());
      setStores((prev) => prev.map((s) => (s.id === id ? { ...s, name: editName.trim() } : s)));
      setEditingId(null);
    } catch {
      // handled by global toast
    }
  }

  function copyUrl(store: StoreData) {
    void navigator.clipboard.writeText(store.accessUrl);
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    setCopiedId(store.id);
    copyTimeoutRef.current = setTimeout(() => setCopiedId(null), 2000);
  }

  function startEdit(store: StoreData) {
    setEditingId(store.id);
    setEditName(store.name);
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-white">{t('stores')}</h2>
      </div>

      {/* Store list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div
            className="w-8 h-8 border-4 rounded-full animate-spin"
            style={{ borderColor: 'rgba(142,152,223,0.3)', borderTopColor: '#8E98DF' }}
          />
        </div>
      ) : stores.length === 0 ? (
        <div
          className="rounded-xl p-12 text-center"
          style={{
            background: 'rgba(142,152,223,0.08)',
            border: '1px solid rgba(142,152,223,0.2)',
          }}
        >
          <svg
            className="w-16 h-16 mx-auto mb-4 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
          <p className="text-gray-400 text-lg">{t('noStores')}</p>
          <p className="text-gray-500 mt-2">{t('noStoresHint')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {stores.map((store) => (
            <div
              key={store.id}
              className="rounded-xl p-6"
              style={{
                background: 'rgba(142,152,223,0.08)',
                border: '1px solid rgba(142,152,223,0.2)',
              }}
            >
              {/* Store header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 mr-4">
                  {editingId === store.id ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void saveEdit(store.id);
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                        className="flex-1 bg-white/5 rounded-lg px-3 py-2 text-white focus:outline-none text-lg font-semibold"
                        style={{ border: '1px solid rgba(142,152,223,0.3)' }}
                        autoFocus
                      />
                      <button
                        onClick={() => void saveEdit(store.id)}
                        className="px-3 py-2 rounded-lg text-white gradient-btn text-sm"
                      >
                        {t('save')}
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="px-3 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20 text-sm"
                      >
                        {t('cancel')}
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-white">{store.name}</h3>
                      {store.isDemo && (
                        <span
                          className="text-xs font-semibold px-2 py-0.5 rounded-full"
                          style={{
                            background: 'rgba(255,251,147,0.15)',
                            color: '#FFFB93',
                            border: '1px solid rgba(255,251,147,0.3)',
                          }}
                        >
                          DEMO
                        </span>
                      )}
                      <button
                        onClick={() => startEdit(store)}
                        className="text-gray-400 hover:text-white p-1"
                        title={t('editStore')}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                  <p className="text-gray-400 text-sm mt-1">
                    {t('createdAt')}:{' '}
                    {new Date(store.createdAt * 1000).toLocaleDateString(i18n.language)}
                  </p>
                </div>
              </div>

              {/* Store URL */}
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-gray-400 text-sm mb-2">{t('storeUrl')}</p>
                <div className="flex items-center gap-2">
                  <code
                    className="flex-1 bg-black/30 rounded px-3 py-2 text-sm break-all"
                    style={{ color: '#29ABE2' }}
                  >
                    {store.accessUrl}
                  </code>
                  <button
                    onClick={() => copyUrl(store)}
                    className="px-3 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20 flex items-center shrink-0"
                    title={t('copy')}
                  >
                    {copiedId === store.id ? (
                      <svg
                        className="w-4 h-4"
                        style={{ color: '#FFFB93' }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    )}
                  </button>
                  <a
                    href={store.accessUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20 flex items-center shrink-0"
                    title={t('open')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
